import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, getAddress } from 'viem'
import { usePublicClient, useReadContracts } from 'wagmi'
import { useMemo } from 'react'
import { ONCHAIN_GC_TIME, ONCHAIN_REFETCH_INTERVAL, ONCHAIN_STALE_TIME } from '@/config/consts'
import type { NormalizedELendInfo } from './types'
import type { PerspectiveSettings } from './elendsQuery'
import {
  useEulerIndexerELends,
  useEulerIndexerDefaultELends,
  useEulerIndexerGovernedELends,
  setELendInCache,
  PERSPECTIVE_ADDRESSES,
  DEFAULT_ENABLED_PERSPECTIVES,
  DEFAULT_DISABLED_PERSPECTIVES,
} from './elendsQuery'
import { fetchELendOnchain, fetchELendsOnchain } from './onchainELendFetcher'
import { E_LEND_CHAIN_ID } from '@/config/consts'
import { getPerspectiveAddresses, SHOW_ALL_ELEND_VAULTS } from '@/config/euler-contracts'
import { eulerEarnGovernedPerspectiveAbi } from 'abis/evm/eulerEarnGovernedPerspective'
import { HYPEREVM_PRODUCTS } from '@/features/evault-details/data/products'

/**
 * Hook to get ELend vault info with indexer-first, onchain-fallback pattern
 *
 * Priority:
 * 1. Check Euler Indexer cache (React Query cache)
 * 2. If not found, fetch from onchain and cache the result
 *
 * This ensures we always have ELend data while preferring the faster indexer
 */
export function useELendInfo(
  address: Address | undefined,
  chainId: number = E_LEND_CHAIN_ID,
  perspectiveSettings?: PerspectiveSettings,
) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const {
    data: vaultMap,
    isLoading: isIndexerLoading,
    isError: isIndexerError,
  } = useEulerIndexerELends(chainId, perspectiveSettings)

  // Check if ELend exists in indexer cache
  const indexerVault = address ? vaultMap?.get(getAddress(address) as Address) : undefined

  // Only fetch from onchain if:
  // 1. We have an address and publicClient
  // 2. Indexer is not loading AND (indexer has error OR vault not found in cache)
  const shouldFetchOnchain = Boolean(address && publicClient && !isIndexerLoading && (isIndexerError || !indexerVault))

  const {
    data: onchainVault,
    isLoading: isOnchainLoading,
    isError: isOnchainError,
  } = useQuery({
    queryKey: ['elend-onchain', chainId, address],
    queryFn: async () => {
      if (!address || !publicClient) throw new Error('No address or publicClient provided')

      const vault = await fetchELendOnchain(publicClient, chainId, address)

      // Cache the result for future lookups
      setELendInCache(queryClient, chainId, vault, perspectiveSettings)

      return vault
    },
    enabled: shouldFetchOnchain,
    staleTime: ONCHAIN_STALE_TIME,
    gcTime: ONCHAIN_GC_TIME,
    refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    retry: 2,
  })

  return {
    vault: indexerVault ?? onchainVault,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isError: isIndexerError && isOnchainError,
    source: indexerVault ? ('indexer' as const) : onchainVault ? ('onchain' as const) : undefined,
  }
}

/**
 * Hook to get multiple ELend vaults with indexer-first, onchain-fallback pattern
 */
export function useELendsInfo(
  addresses: Address[],
  chainId: number = E_LEND_CHAIN_ID,
  perspectiveSettings?: PerspectiveSettings,
) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })

  // First, try to get from Euler Indexer cache
  const {
    data: vaultMap,
    isLoading: isIndexerLoading,
    isError: isIndexerError,
  } = useEulerIndexerELends(chainId, perspectiveSettings)

  // Find which vaults are missing from cache
  const missingAddresses = addresses.filter((address) => {
    if (!vaultMap) return true
    try {
      return !vaultMap.has(getAddress(address) as Address)
    } catch {
      return true
    }
  })

  // Fetch missing vaults from onchain
  const shouldFetchOnchain = !isIndexerLoading && missingAddresses.length > 0 && !!publicClient

  const { data: onchainVaults, isLoading: isOnchainLoading } = useQuery({
    queryKey: ['elends-onchain', chainId, missingAddresses],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const vaults = await fetchELendsOnchain(publicClient, chainId, missingAddresses)

      // Cache all fetched vaults
      for (const vault of vaults.values()) {
        setELendInCache(queryClient, chainId, vault, perspectiveSettings)
      }

      return vaults
    },
    enabled: shouldFetchOnchain,
    staleTime: ONCHAIN_STALE_TIME,
    gcTime: ONCHAIN_GC_TIME,
    refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    retry: 2,
  })

  // Combine indexer and onchain results
  const vaults = new Map<Address, NormalizedELendInfo>()

  for (const address of addresses) {
    try {
      const normalizedAddress = getAddress(address) as Address

      // Check indexer first
      const indexerVault = vaultMap?.get(normalizedAddress)
      if (indexerVault) {
        vaults.set(normalizedAddress, indexerVault)
        continue
      }

      // Check onchain fallback
      const onchainVault = onchainVaults?.get(normalizedAddress)
      if (onchainVault) {
        vaults.set(normalizedAddress, onchainVault)
      }
    } catch {
      // Invalid address, skip
    }
  }

  return {
    vaults,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isIndexerError,
    missingCount: addresses.length - vaults.size,
  }
}

/**
 * Helper to get ELend vault from a vaults map by address
 * Handles address normalization and error handling
 */
export function getELendFromMap(
  vaults: Map<Address, NormalizedELendInfo>,
  address: Address | undefined,
): NormalizedELendInfo | undefined {
  if (!address) return undefined
  try {
    const normalizedAddress = getAddress(address) as Address
    return vaults.get(normalizedAddress)
  } catch {
    return undefined
  }
}

/**
 * Get all ELend vault addresses from the indexer cache
 */
export function useELendAddresses(
  chainId: number = E_LEND_CHAIN_ID,
  perspectiveSettings?: PerspectiveSettings,
): Address[] {
  const { data: vaultMap } = useEulerIndexerELends(chainId, perspectiveSettings)
  return vaultMap ? Array.from(vaultMap.keys()) : []
}

/**
 * Get ELends array from indexer (useful for displaying lists)
 * WITH ONCHAIN FALLBACK: Falls back to useDefaultELendsList when indexer is down
 * WITH ORACLE PRICE FALLBACK: Fetches oracle prices for assets missing price data
 */
export function useELendsList(chainId: number = E_LEND_CHAIN_ID, perspectiveSettings?: PerspectiveSettings) {
  const {
    data: vaultMap,
    isLoading: isIndexerLoading,
    isError: isIndexerError,
  } = useEulerIndexerELends(chainId, perspectiveSettings)
  const publicClient = usePublicClient({ chainId })

  const indexerVaults = useMemo(() => (vaultMap ? Array.from(vaultMap.values()) : []), [vaultMap])

  // Onchain fallback: only enabled when indexer fails AND has no cached data.
  // If the indexer errored but vaultMap still holds valid cached results (from a previous
  // successful fetch), prefer those over potentially empty/partial onchain fallback data.
  // Note: useDefaultELendsList uses the default perspective set (governed + edge factory),
  // so the caller's perspectiveSettings are not forwarded. This is acceptable because
  // no current caller passes perspectiveSettings, and in degraded mode best-effort data is preferred.
  const hasIndexerData = indexerVaults.length > 0
  const shouldUseFallback = !isIndexerLoading && isIndexerError && !hasIndexerData
  const { vaults: fallbackVaults, isPending: isFallbackPending } = useDefaultELendsList(
    chainId,
    shouldUseFallback,
    true,
  )

  const vaults = shouldUseFallback ? fallbackVaults : indexerVaults

  // Find vaults that need oracle price fallback (only for indexer data;
  // fallback data from useDefaultELendsList is already oracle-enriched)
  const needsPriceFallback = useMemo(() => {
    return vaults.some((vault) => vault.asset && (vault.assetPrice === null || vault.assetPrice === 0))
  }, [vaults])

  // Fetch oracle prices for assets missing prices — skip when using fallback
  // since useDefaultELendsList already runs its own oracle enrichment
  const { data: enrichedVaults } = useQuery({
    queryKey: ['elend-oracle-price-fallback', chainId, vaults.map((v) => v.address).join(',')],
    queryFn: async () => {
      if (!publicClient) return vaults

      const { enrichELendsWithOraclePrices } = await import('./onchainELendFetcher')
      return enrichELendsWithOraclePrices(publicClient, vaults)
    },
    enabled: !shouldUseFallback && vaults.length > 0 && needsPriceFallback && !!publicClient,
    staleTime: 60_000, // 1 minute
    placeholderData: vaults, // Use un-enriched data while loading
  })

  const isLoading = isIndexerLoading || (shouldUseFallback && isFallbackPending)
  // Only report error if indexer failed AND fallback finished without providing data
  const isError = isIndexerError && !isFallbackPending && fallbackVaults.length === 0

  return {
    vaults: enrichedVaults || vaults,
    isLoading,
    isError,
  }
}

/**
 * Get ELends with configurable perspective filtering (via SHOW_ALL_ELEND_VAULTS config)
 * WITH ONCHAIN FALLBACK: If indexer fails, fetches vault addresses from perspective contracts
 * and gets vault details from onchain
 * @param enabled - When false, skips all fetching (indexer + onchain). Defaults to true.
 * @param skipIndexer - When true, skips the indexer query and goes straight to onchain.
 *   Use this when the caller already knows the indexer is down to avoid a redundant failure.
 */
export function useDefaultELendsList(chainId: number = E_LEND_CHAIN_ID, enabled = true, skipIndexer = false) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })
  const perspectiveAddresses = getPerspectiveAddresses(chainId)

  // Step 1: Try to get from Euler Indexer (skipped when caller already knows indexer is down)
  const indexerEnabled = enabled && !skipIndexer
  const {
    data: vaultMap,
    isPending: isPendingIndexer,
    isError: isIndexerError,
  } = useEulerIndexerDefaultELends(chainId, indexerEnabled)

  // Step 2: If indexer fails (or was skipped), get vault addresses from perspective contracts onchain
  const indexerUnavailable = skipIndexer || (isIndexerError && !isPendingIndexer)
  const shouldFetchPerspectiveAddresses = enabled && indexerUnavailable && !!perspectiveAddresses

  // Note: escrowedCollateral excluded - escrow vaults are internal to Earn vaults
  const perspectiveContracts = perspectiveAddresses
    ? [
        {
          address: perspectiveAddresses.governed,
          abi: eulerEarnGovernedPerspectiveAbi,
          functionName: 'verifiedArray',
          chainId,
        },
        {
          address: perspectiveAddresses.edgeFactory,
          abi: eulerEarnGovernedPerspectiveAbi,
          functionName: 'verifiedArray',
          chainId,
        },
      ]
    : []

  const { data: perspectiveResults, isPending: isPendingPerspective } = useReadContracts({
    contracts: perspectiveContracts as any,
    query: {
      enabled: shouldFetchPerspectiveAddresses,
      staleTime: 60_000, // 1 minute
    },
  })

  // Combine vault addresses from all enabled perspectives (excluding duplicates)
  const vaultAddressesFromPerspectives: Address[] = []
  if (perspectiveResults) {
    const seenAddresses = new Set<string>()
    for (const result of perspectiveResults) {
      if (result.status === 'success' && Array.isArray(result.result)) {
        for (const addr of result.result as Address[]) {
          const normalized = addr.toLowerCase()
          if (!seenAddresses.has(normalized)) {
            seenAddresses.add(normalized)
            vaultAddressesFromPerspectives.push(addr)
          }
        }
      }
    }
  }

  // Step 3: If we have vault addresses from perspectives, fetch vault details onchain
  const shouldFetchOnchain =
    enabled && indexerUnavailable && vaultAddressesFromPerspectives.length > 0 && !!publicClient

  const { data: onchainVaults, isPending: isPendingOnchain } = useQuery({
    queryKey: ['elends-list-onchain-fallback', chainId],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const vaults = await fetchELendsOnchain(publicClient, chainId, vaultAddressesFromPerspectives)

      // Cache all fetched vaults
      for (const vault of vaults.values()) {
        setELendInCache(queryClient, chainId, vault, undefined)
      }

      return vaults
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000, // 10 minutes for onchain data
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  // Dev mode: collect all product vault addresses to ensure they're fetched
  const allProductVaultAddresses = useMemo(() => {
    if (!SHOW_ALL_ELEND_VAULTS) return []
    const addresses: Address[] = []
    for (const product of HYPEREVM_PRODUCTS.values()) {
      for (const v of product.vaults) {
        addresses.push(getAddress(v) as Address)
      }
    }
    return addresses
  }, [])

  // Dev mode: find which product vaults are missing from the indexer response
  // Only narrow the list once the indexer has settled to keep the query key stable
  const missingProductVaultAddresses = useMemo(() => {
    if (!SHOW_ALL_ELEND_VAULTS || allProductVaultAddresses.length === 0) return allProductVaultAddresses
    if (vaultMap === undefined) return allProductVaultAddresses
    const existing = new Set(Array.from(vaultMap.keys()).map((a) => a.toLowerCase()))
    return allProductVaultAddresses.filter((a) => !existing.has(a.toLowerCase()))
  }, [allProductVaultAddresses, vaultMap])

  // Dev mode: fetch missing product vaults from on-chain (runs in parallel with indexer)
  const shouldFetchDevVaults =
    enabled && SHOW_ALL_ELEND_VAULTS && missingProductVaultAddresses.length > 0 && !!publicClient
  const { data: devOnchainVaults } = useQuery({
    queryKey: ['elends-dev-product-fallback', chainId, missingProductVaultAddresses.join(',')],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')
      return fetchELendsOnchain(publicClient, chainId, missingProductVaultAddresses)
    },
    enabled: shouldFetchDevVaults,
    staleTime: 60_000,
    retry: 1,
  })

  // Merge indexer/onchain data with dev product vaults
  const mergedVaultMap = useMemo(() => {
    const base = vaultMap || onchainVaults
    if (!devOnchainVaults || devOnchainVaults.size === 0) return base
    const merged = new Map(base || new Map<Address, NormalizedELendInfo>())
    for (const [addr, vault] of devOnchainVaults) {
      if (!merged.has(addr)) {
        merged.set(addr, vault)
      }
    }
    return merged
  }, [vaultMap, onchainVaults, devOnchainVaults])

  // Return indexer data if available, otherwise onchain data, merged with dev vaults
  const finalVaultMap = mergedVaultMap
  const vaults = useMemo(() => (finalVaultMap ? Array.from(finalVaultMap.values()) : []), [finalVaultMap])

  // Find vaults that need oracle price fallback
  const needsPriceFallback = useMemo(() => {
    return vaults.some((vault) => vault.asset && (vault.assetPrice === null || vault.assetPrice === 0))
  }, [vaults])

  // Fetch oracle prices for assets missing prices
  const { data: enrichedVaults } = useQuery({
    queryKey: ['elend-default-oracle-price-fallback', chainId, vaults.map((v) => v.address).join(',')],
    queryFn: async () => {
      if (!publicClient) return vaults

      const { enrichELendsWithOraclePrices } = await import('./onchainELendFetcher')
      return enrichELendsWithOraclePrices(publicClient, vaults)
    },
    enabled: enabled && needsPriceFallback && !!publicClient,
    staleTime: 60_000, // 1 minute
    placeholderData: vaults, // Use un-enriched data while loading
  })

  // Only show error if indexer unavailable AND onchain failed AND we have vault addresses
  // If no vault addresses, it's not an error - just no vaults to display
  const hasVaultAddresses = vaultAddressesFromPerspectives.length > 0
  const shouldShowError = indexerUnavailable && !onchainVaults && hasVaultAddresses

  return {
    vaults: enrichedVaults || vaults,
    isPending:
      (indexerEnabled && isPendingIndexer) ||
      (shouldFetchPerspectiveAddresses && isPendingPerspective) ||
      (shouldFetchOnchain && isPendingOnchain),
    isError: shouldShowError,
    error: shouldShowError ? new Error('Failed to load vaults') : null,
  }
}

/**
 * Get governed ELends (vaults verified by the governed perspective only)
 * WITH ONCHAIN FALLBACK: If indexer fails, fetches vault addresses from governed perspective
 * and gets vault details from onchain
 */
export function useGovernedELendsList(chainId: number = E_LEND_CHAIN_ID) {
  const queryClient = useQueryClient()
  const publicClient = usePublicClient({ chainId })
  const perspectiveAddresses = getPerspectiveAddresses(chainId)

  // Step 1: Try to get from Euler Indexer
  const {
    data: vaultMap,
    isLoading: isIndexerLoading,
    isError: isIndexerError,
  } = useEulerIndexerGovernedELends(chainId)

  // Step 2: If indexer fails, get vault addresses from governed perspective contract onchain
  const shouldFetchPerspectiveAddresses = isIndexerError && !isIndexerLoading && !!perspectiveAddresses

  const { data: perspectiveResults } = useReadContracts({
    contracts: [
      {
        address: perspectiveAddresses?.governed,
        abi: eulerEarnGovernedPerspectiveAbi,
        functionName: 'verifiedArray',
        chainId,
      },
    ] as any,
    query: {
      enabled: shouldFetchPerspectiveAddresses,
      staleTime: 60_000, // 1 minute
    },
  })

  // Get vault addresses from governed perspective
  const vaultAddressesFromPerspective: Address[] =
    perspectiveResults?.[0]?.status === 'success' ? (perspectiveResults[0].result as Address[]) : []

  // Step 3: If we have vault addresses from perspective, fetch vault details onchain
  const shouldFetchOnchain =
    isIndexerError && !isIndexerLoading && vaultAddressesFromPerspective.length > 0 && !!publicClient

  const { data: onchainVaults, isLoading: isOnchainLoading } = useQuery({
    queryKey: ['elends-governed-onchain-fallback', chainId],
    queryFn: async () => {
      if (!publicClient) throw new Error('No publicClient provided')

      const vaults = await fetchELendsOnchain(publicClient, chainId, vaultAddressesFromPerspective)

      // Cache all fetched vaults
      for (const vault of vaults.values()) {
        setELendInCache(queryClient, chainId, vault, undefined)
      }

      return vaults
    },
    enabled: shouldFetchOnchain,
    staleTime: 10 * 60 * 1000, // 10 minutes for onchain data
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  // Return indexer data if available, otherwise onchain data
  const finalVaultMap = vaultMap || onchainVaults
  const vaults = useMemo(() => (finalVaultMap ? Array.from(finalVaultMap.values()) : []), [finalVaultMap])

  // Find vaults that need oracle price fallback
  const needsPriceFallback = useMemo(() => {
    return vaults.some((vault) => vault.asset && (vault.assetPrice === null || vault.assetPrice === 0))
  }, [vaults])

  // Fetch oracle prices for assets missing prices
  const { data: enrichedVaults } = useQuery({
    queryKey: ['elend-governed-oracle-price-fallback', chainId, vaults.map((v) => v.address).join(',')],
    queryFn: async () => {
      if (!publicClient) return vaults

      const { enrichELendsWithOraclePrices } = await import('./onchainELendFetcher')
      return enrichELendsWithOraclePrices(publicClient, vaults)
    },
    enabled: !isIndexerLoading && needsPriceFallback && !!publicClient,
    staleTime: 60_000, // 1 minute
    placeholderData: vaults, // Use un-enriched data while loading
  })

  // Only show error if indexer failed AND onchain failed AND we have vault addresses
  // If no vault addresses, it's not an error - just no vaults to display
  const hasVaultAddresses = vaultAddressesFromPerspective.length > 0
  const shouldShowError = isIndexerError && !onchainVaults && hasVaultAddresses

  return {
    vaults: enrichedVaults || vaults,
    isLoading: isIndexerLoading || (shouldFetchOnchain && isOnchainLoading),
    isError: shouldShowError,
    error: shouldShowError ? new Error('Failed to load vaults') : null,
  }
}

// Re-export for convenience
export { PERSPECTIVE_ADDRESSES, DEFAULT_ENABLED_PERSPECTIVES, DEFAULT_DISABLED_PERSPECTIVES }
